import {CushlaGetQueryOptions, endpointKeys} from '@common/api'
import {
  Availabilities,
  ServiceInfo,
  BookAppointmentRequest,
  BookAppointmentResponse,
} from '@generated/models'
import {PatientAppointmentsResponse} from '@common/models'
import {
  availabilitiesNormaliser,
  bookAppointmentResponseNormaliser,
  serviceInfoNormaliser,
  patientAppointmentsResponseNormaliser,
} from '@common/normalisers'

import {useApi, useWebGet, useWebSet, useWebVariableUrlSet} from './helpers'

export const useGetBookingsPortalServiceInfo = (partnerServiceId: string) =>
  useWebGet<ServiceInfo>(
    [endpointKeys.bookingsPortalServiceInfo, {partnerServiceId}],
    useApi().bookingsPortalServiceInfo({partnerServiceId}),
    serviceInfoNormaliser,
    {},
    false, // no auth headers
  )

export const useGetBookingsPortalAvailabilities = (
  partnerServiceId: string,
  monthSpec: string,
  options?: CushlaGetQueryOptions<Availabilities>,
) =>
  useWebGet<Availabilities>(
    [endpointKeys.bookingsPortalAvailabilities, {partnerServiceId, monthSpec}],
    useApi().bookingsPortalAvailabilities({partnerServiceId, monthSpec}),
    availabilitiesNormaliser,
    options ?? {},
    false, // no auth headers
  )

export const useBookingsPortalBookAppointment = (
  partnerServiceId: string,
  monthSpec: string,
) =>
  useWebSet<BookAppointmentRequest, BookAppointmentResponse>(
    [endpointKeys.bookingsPortalBookAppointment, {partnerServiceId}],
    useApi().bookingsPortalBookAppointment({partnerServiceId}),
    'POST',
    bookAppointmentResponseNormaliser,
    [
      [
        endpointKeys.bookingsPortalAvailabilities,
        {partnerServiceId, monthSpec},
      ],
      [endpointKeys.bookingsPortalServiceInfo, {partnerServiceId}],
    ],
    {
      // Suppress default error notification; page will handle errors explicitly
      onError: () => undefined,
    },
    false,
  )

export const useGetBookingsPortalPatientAppointments = (
  patientId: string,
  options?: CushlaGetQueryOptions<PatientAppointmentsResponse>,
) =>
  useWebGet<PatientAppointmentsResponse>(
    [endpointKeys.bookingsPortalPatientAppointments, {patientId}],
    useApi().bookingsPortalPatientAppointments({patientId}),
    patientAppointmentsResponseNormaliser,
    options ?? {},
    false,
  )

export const useBookingsPortalCancelAppointment = (patientId: string) => {
  const api = useApi()
  return useWebVariableUrlSet<
    {partnerServiceId: string; partnerServiceSlotId: string; comment?: string},
    Record<string, never>,
    void
  >(
    [endpointKeys.bookingsPortalCancelAppointment],
    ({partnerServiceId, partnerServiceSlotId, comment}) => {
      const url = api.bookingsPortalCancelAppointment({
        partnerServiceId,
        partnerServiceSlotId,
      })
      if (comment) {
        url.searchParams.set('comment', comment)
      }
      return url
    },
    'DELETE',
    (data) => data as unknown as void,
    {},
    [[endpointKeys.bookingsPortalPatientAppointments, {patientId}]],
    false,
  )
}

export const useBookingsPortalRescheduleAppointment = (patientId: string) => {
  const api = useApi()
  return useWebVariableUrlSet<
    {partnerServiceId: string; fromPartnerServiceSlotId: string},
    {date: string; time: string; comment?: string | null},
    BookAppointmentResponse
  >(
    [endpointKeys.bookingsPortalRescheduleAppointment],
    ({partnerServiceId, fromPartnerServiceSlotId}) =>
      api.bookingsPortalRescheduleAppointment({
        partnerServiceId,
        fromPartnerServiceSlotId,
      }),
    'POST',
    bookAppointmentResponseNormaliser,
    {},
    [[endpointKeys.bookingsPortalPatientAppointments, {patientId}]],
    false,
  )
}
